import React, { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { useForm, SubmitHandler } from "react-hook-form";
import MainLayout from "../layout/main";
import {
	createItem,
	createProduct,
	deleteItem,
	getItemsByProduct,
	getRentalsByItem,
	getProduct,
	updateItem,
	updateProduct,
	uploadFile
} from "../service/callAPI";
import { handleFileInput } from "../lib/fileInput";
import Schema, { ProductStatus } from "../type/schema";
import { useLoadState } from "../component/hook";
import { toast } from "react-toastify";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import ValidatorSpan from "../component/validatorSpan";
import Card from "../component/card";
import { headingStyle } from "../theme/default";
import { accessiblePath } from "../lib/misc";

type Inputs = {
	id: string;
	sku: string;
	thumbnailUrl: string;
	photoUrls: string[];
	name: string;
	price: number;
	description: string;
};

const labelStyle = {
	fontWeight: "bold" as "bold",
	color: "#4e73df"
};

export default function ProductEditPage() {
	const { productId } = useParams<{ productId: string }>();

	return (
		<MainLayout pageTitle="Sản Phẩm">
			<ProductForm productId={productId} />
			<hr />
			<Stock productId={productId} />
		</MainLayout>
	);
}

const ProductForm = ({ productId }: { productId: string }) => {
	const [thumbnailBase64, setThumbnailBase64] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const { register, handleSubmit, watch, formState, setValue } =
		useForm<Inputs>();
	const { isDirty, errors } = formState;

	const fetcher = useCallback(() => {
		if (!productId) {
			return;
		}
		return getProduct(productId);
	}, [productId]);
	const callback = useCallback(
		(data: Schema.Product) => {
			const converted = initialConvert(data);
			for (let [key, val] of Object.entries(converted)) {
				setValue(key as any, val);
			}
			converted.thumbnailUrl &&
				setThumbnailBase64(accessiblePath(converted.thumbnailUrl));
		},
		[setValue]
	);
	useLoadState(fetcher, callback);

	const onSubmit: SubmitHandler<Inputs> = async (data) => {
		try {
			setIsLoading(true);
			const formatted = finalConvert(data);
			if (!formatted.id) {
				await handleCreateProduct(formatted);
				return;
			}
			await handleUpdateProduct(formatted);
		} catch (e: any) {
			const message = e?.response?.data || e.toString();
			toast.error(message);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<React.Fragment>
			<h1 className="h3 mb-4 text-gray-800">
				<span style={{ fontWeight: "bold", color: "#4e73df" }}>Sản Phẩm: </span>
				{watch("name")}
			</h1>
			<Form
				className="shadow mb-5"
				style={{
					backgroundColor: "white",
					padding: "20px",
					borderRadius: "0.35rem"
				}}
				onSubmit={handleSubmit(onSubmit)}
			>
				<InputGroup className="mb-3">
					<InputGroup.Prepend>
						<InputGroup.Text style={labelStyle}>ID</InputGroup.Text>
					</InputGroup.Prepend>
					<Form.Control readOnly {...register("id")} />
				</InputGroup>
				<Row>
					<Col sm={6}>
						<Form.Group>
							<Form.Label style={labelStyle}>Tên sản phẩm:</Form.Label>
							<Form.Control
								{...register("name", {
									required: "Không được để trống trường này"
								})}
							/>
							{errors.name && <ValidatorSpan message={errors.name.message} />}
						</Form.Group>

						<Form.Group>
							<Form.Label style={labelStyle}>Giá:</Form.Label>
							<Form.Control
								type="number"
								{...register("price", {
									required: "Không được để trống trường này"
								})}
							/>
							{errors.price && <ValidatorSpan message={errors.price.message} />}
						</Form.Group>

						<Form.Group>
							<Form.Label style={labelStyle}>Mô tả ngắn:</Form.Label>
							<Form.Control
								as="textarea"
								rows={3}
								{...register("description")}
							/>
							{errors.description && (
								<ValidatorSpan message={errors.description.message} />
							)}
						</Form.Group>
					</Col>
					<Col sm={6}>
						<Form.Group>
							<Form.Label style={labelStyle}>SKU:</Form.Label>
							<Form.Control
								type="number"
								{...register("sku", {
									required: "Không được để trống trường này"
								})}
							/>
							{errors.sku && <ValidatorSpan message={errors.sku.message} />}
						</Form.Group>

						<Form.Group>
							<Form.Label style={labelStyle}>Ảnh bìa:</Form.Label>
							<div>
								{thumbnailBase64 && (
									<img
										className="mb-2"
										src={thumbnailBase64}
										style={{ maxHeight: "130px" }}
										alt="Product"
									/>
								)}
							</div>
							<Form.Control
								type="file"
								onChange={handleFileInput((base64) => {
									setThumbnailBase64(base64);
									uploadFile({
										dir: "/products",
										base64String: base64
									})
										.then((resp) =>
											setValue("thumbnailUrl", resp.url, { shouldDirty: true })
										)
										.catch((e: any) => {
											const message = e?.response?.data || e.toString();
											toast.error(message);
										});
								})}
							/>
						</Form.Group>
					</Col>
				</Row>

				<div style={{ textAlign: "center" }}>
					<Button
						className="m-2"
						variant="primary"
						type="submit"
						disabled={!isDirty}
					>
						{isLoading ? "Đang lưu..." : "Lưu"}
					</Button>
					<a className="m-2 btn btn-danger" href="/product">
						Quay Lại
					</a>
				</div>
			</Form>
		</React.Fragment>
	);
};

function initialConvert(data: Schema.Product) {
	const categories = Array.isArray(data.categories)
		? data.categories.join(", ")
		: "";

	return { ...data, categories };
}

function finalConvert(data: Inputs): Schema.Product {
	return data as unknown as Schema.Product;
}

async function handleCreateProduct(payload: Schema.Product): Promise<void> {
	const resp = await createProduct(payload);
	toast.info("Tạo sản phẩm mới thành công");
	setTimeout(() => (window.location.href = `/product/${resp.id}`), 5000);
}

async function handleUpdateProduct(payload: Schema.Product): Promise<void> {
	await updateProduct(payload);
	toast.info("Cập nhật sản phẩm thành công");
}

const Stock = ({ productId }: { productId: string }) => {
	const [items, setItems] = useState<Schema.Item[]>([]);
	const fetcher = useCallback(() => getItemsByProduct(productId), [productId]);
	useLoadState(fetcher, setItems);

	const handleDelete = async (id: string) => {
		try {
			await deleteItem(id);
			setItems((items) => items.filter((item) => item.id !== id));
		} catch (e: any) {
			const message = e?.response?.data || e.toString();
			toast.error(message);
		}
	};

	return (
		<div>
			<Row className="mb-4">
				<Col>
					<h3 style={headingStyle}>Kho hàng:</h3>
				</Col>
				<Col>
					<Button
						variant="success"
						style={{ float: "right" }}
						onClick={async () => {
							try {
								const newItem = {
									id: uuid(),
									productId,
									status: ProductStatus.AVAILABLE,
									note: ""
								};
								await createItem(newItem);
								setItems((items) => {
									return [...items, newItem];
								});
							} catch (e: any) {
								const message = e?.response?.data || e.toString();
								toast.error(message);
							}
						}}
					>
						Thêm mới
					</Button>
				</Col>
			</Row>
			<Row>
				{items.length > 0 &&
					items.map((item) => (
						<Col key={item.id} sm={4} className="mb-3">
							<Item item={item} handleDelete={handleDelete} />
						</Col>
					))}
			</Row>
		</div>
	);
};

type ItemFormInputs = {
	id: string;
	status: ProductStatus;
	note: string;
};

const Item = ({
	item,
	handleDelete
}: {
	item: Schema.Item;
	handleDelete: Function;
}) => {
	const { register, handleSubmit } = useForm<ItemFormInputs>();
	const saveItem: SubmitHandler<ItemFormInputs> = async (data) => {
		try {
			await updateItem(data as unknown as Schema.Item);
			toast.info("Lưu thành công");
		} catch (e: any) {
			const message = e?.response?.data || e.toString();
			toast.error(message);
		} finally {
		}
	};

	const [rentals, setRentals] = useState<Schema.Rental[]>([]);
	useLoadState(
		useCallback(() => getRentalsByItem(item.id), [item.id]),
		useCallback((data: { rentals: Schema.Rental[] }) => {
			const notDone = data.rentals.filter((r) => !r.isDone);
			setRentals(notDone);
		}, [])
	);

	const productStatusOpts = Object.entries(ProductStatus).filter(([key]) =>
		isNaN(Number(key))
	);

	return (
		<Card key={item.id} title={item.id}>
			<Form onSubmit={handleSubmit(saveItem)}>
				<input type="hidden" {...register("id")} defaultValue={item.id} />
				<Row>
					<Col sm="6">
						<p style={labelStyle}>Trạng thái:</p>
					</Col>
					<Col sm="6">
						<Form.Control
							as="select"
							{...register("status")}
							defaultValue={item.status}
						>
							{productStatusOpts.map(([key, value]) => (
								<option key={value} value={value}>
									{key}
								</option>
							))}
						</Form.Control>
					</Col>
				</Row>
				<Row>
					<Col sm="6">
						<p style={labelStyle}>Đơn hàng:</p>
					</Col>
					<Col sm="6">
						{rentals.map((r) => (
							<p>
								<a key={r.id} href={`/rental/${r.id}`}>
									{r.id.slice(0, 8)}
								</a>
							</p>
						))}
					</Col>
				</Row>
				<Row>
					<Col sm="6">
						<p style={labelStyle}>Chú thích:</p>
					</Col>
					<Col sm="6">
						<Form.Control
							as="textarea"
							defaultValue={item.note}
							{...register("note")}
						></Form.Control>
					</Col>
				</Row>
				<div className="text-right mt-3">
					<Button className="mr-2" variant="primary" type="submit">
						Lưu
					</Button>
					<Button
						variant="danger"
						onClick={handleDelete.bind(null, item.id) as any}
					>
						Xóa
					</Button>
				</div>
			</Form>
		</Card>
	);
};
